
import React, { useState, useEffect } from 'react';
import { View, Text, TextInput, StyleSheet, KeyboardAvoidingView, ScrollView, Platform } from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import Toast from '@/components/Toast';
import { useKeyboardVisible } from '@/hooks/useKeyboardVisible';
import PrimaryButton from '@/components/PrimaryButton';
import PageHeader from '@/components/PageHeader';

export default function ChangePhoneScreen() {
  const router = useRouter();
  const { t } = useLanguage();
  const insets = useSafeAreaInsets();
  const keyboardVisible = useKeyboardVisible();
  const { user, userProfile, updateProfile } = useAuth();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<1 | 2>(1);
  const [isFirstTime, setIsFirstTime] = useState(true);
  const [currentPhone, setCurrentPhone] = useState('');
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info'>('success');

  useEffect(() => {
    // Check if user already has a phone number
    if (userProfile?.phone) {
      setIsFirstTime(false);
      setCurrentPhone(userProfile.phone);
      // If user has a phone, start with password verification (step 1)
      setStep(1);
    } else {
      // If it's first time, skip to step 2 (phone input)
      setIsFirstTime(true);
      setStep(2);
    }
  }, [userProfile]);

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setToastVisible(true);
  };

  const hideToast = () => {
    setToastVisible(false);
  };

  const handleClose = () => {
    router.back();
  };

  const formatPhoneNumber = (phone: string) => {
    // Remove all non-digits
    const digits = phone.replace(/\D/g, '');

    // Format as (XX) XXXXX-XXXX
    if (digits.length <= 2) {
      return `(${digits}`;
    } else if (digits.length <= 7) {
      return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    } else {
      return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
    }
  };

  const handlePhoneChange = (text: string) => {
    const formatted = formatPhoneNumber(text);
    if (formatted.replace(/\D/g, '').length <= 11) {
      setPhoneNumber(formatted);
    }
  };

  const validatePhoneNumber = (phone: string) => {
    const digits = phone.replace(/\D/g, '');
    return digits.length === 11;
  };

  const handleVerifyPassword = async () => {
    if (!password) {
      showToast(t('account.changePhone.errorPasswordRequired'), 'error');
      return;
    }

    setLoading(true);

    try {
      const { supabase } = await import('@/lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        showToast(t('common.error'), 'error');
        return;
      }

      const response = await fetch('https://mobile.padrinho.app/api/auth/verify-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ password }),
      });

      const data = await response.json();

      if (!data.success) {
        if (data.error === 'too_many_attempts') {
          showToast(t('account.changeEmail.errorTooManyAttempts'), 'error');
        } else {
          showToast(t('account.changePhone.errorPassword'), 'error');
        }
        return;
      }

      setStep(2);
    } catch (error) {
      console.error('Error verifying password:', error);
      showToast(t('account.changePhone.errorPassword'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePhone = async () => {
    if (!validatePhoneNumber(phoneNumber)) {
      showToast(t('account.changePhone.errorInvalidPhone'), 'error');
      return;
    }

    if (!isFirstTime && phoneNumber === currentPhone) {
      showToast(t('account.changePhone.errorSamePhone'), 'error');
      return;
    }

    setLoading(true);

    try {
      // Update profile with new phone number in Supabase
      const { error } = await updateProfile({ phone: phoneNumber });
      
      if (error) {
        console.error('Error updating phone:', error);
        showToast(t('account.changePhone.errorGeneric'), 'error');
        return;
      }

      console.log('Phone number updated:', phoneNumber);

      // PRD §5.10 — `settings_changed` (`field=phone`). Success path only.
      import('@/utils/clarityEvents')
        .then(({ logSettingsChanged }) => logSettingsChanged({ field: 'phone' }))
        .catch(() => {});

      // Navigate back to profile page where toast will be shown
      router.back();

    } catch (error) {
      console.error('Error updating phone:', error);
      showToast(t('account.changePhone.errorGeneric'), 'error');
    } finally {
      setLoading(false);
    }
  };

  // Step 1: Password verification (only for existing phone users)
  if (step === 1 && !isFirstTime) {
    return (
      <SafeAreaView style={styles.container} edges={['top']}>
        <PageHeader 
          title={t('account.changePhone.title')} 
          onBack={handleClose}
          backgroundColor="#FFFFFF"
        />

        <KeyboardAvoidingView 
          style={styles.keyboardAvoidingView} 
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 20}
        >
          <ScrollView 
            style={styles.scrollView}
            contentContainerStyle={styles.scrollViewContent}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
          >
            <View style={styles.content}>
              <Text style={styles.description}>
                {t('account.changePhone.subtitle')}
              </Text>

              <View style={styles.form}>
                <View style={styles.inputGroup}>
                  <Text style={styles.label}>{t('account.changePhone.currentPassword')}</Text>
                  <TextInput
                    style={[styles.input, { color: '#151515' }]}
                    value={password}
                    onChangeText={setPassword}
                    autoFocus
                    placeholder={t('account.changePhone.passwordPlaceholder')}
                    placeholderTextColor="#A19F97"
                    secureTextEntry
                  />
                </View>
              </View>
            </View>
          </ScrollView>

          <View style={[styles.footer, { paddingBottom: keyboardVisible ? 16 : insets.bottom + 24 }]}>
            <PrimaryButton
              title={loading ? t('account.changePhone.buttonLoading') : t('account.changePhone.buttonContinue')}
              onPress={handleVerifyPassword}
              disabled={loading || !password}
            />
          </View>
        </KeyboardAvoidingView>

        <Toast
          visible={toastVisible}
          type={toastType}
          message={toastMessage}
          onHide={hideToast}
          duration={4000}
        />
      </SafeAreaView>
    );
  }

  // Step 2: Phone number input
  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <PageHeader 
        title={isFirstTime ? t('account.changePhone.titleSet') : t('account.changePhone.title')} 
        onBack={handleClose}
        backgroundColor="#FFFFFF"
      />

      <KeyboardAvoidingView 
        style={styles.keyboardAvoidingView} 
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 20}
      >
        <ScrollView 
          style={styles.scrollView}
          contentContainerStyle={styles.scrollViewContent}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          <View style={styles.content}>
            <Text style={styles.description}>
              {isFirstTime 
                ? t('account.changePhone.subtitleFirst')
                : t('account.changePhone.subtitle')
              }
            </Text>

            {!isFirstTime && (
              <View style={styles.currentPhoneContainer}>
                <Text style={styles.currentPhoneLabel}>{t('account.changePhone.currentPhone')}</Text>
                <Text style={styles.currentPhone}>{currentPhone}</Text>
              </View>
            )}

            <View style={styles.form}>
              <View style={styles.inputGroup}>
                <Text style={styles.label}>
                  {isFirstTime ? t('account.changePhone.label') : t('account.changePhone.labelNew')}
                </Text>
                <TextInput
                  style={[styles.input, { color: '#151515' }]}
                  value={phoneNumber}
                  onChangeText={handlePhoneChange}
                  autoFocus
                  placeholder={t('account.changePhone.phonePlaceholder')}
                  placeholderTextColor="#A19F97"
                  keyboardType="numeric"
                />
                <Text style={styles.inputHint}>
                  {t('account.changePhone.phoneHint')}
                </Text>
              </View>
            </View>
          </View>
        </ScrollView>

        <View style={[styles.footer, { paddingBottom: keyboardVisible ? 16 : insets.bottom + 24 }]}>
          <PrimaryButton
            title={loading ? t('account.changePhone.buttonLoading') : (isFirstTime ? t('account.changePhone.buttonSet') : t('account.changePhone.button'))}
            onPress={handleUpdatePhone}
            disabled={loading || !validatePhoneNumber(phoneNumber)}
          />
        </View>
      </KeyboardAvoidingView>

      <Toast
        visible={toastVisible}
        type={toastType}
        message={toastMessage}
        onHide={hideToast}
        duration={4000}
      />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  keyboardAvoidingView: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollViewContent: {
    flexGrow: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#F0EEE4',
  },
  backButton: {
    padding: 4,
  },
  headerTitle: {
    fontSize: 14,
    fontFamily: 'Inter',
    fontWeight: 'bold',
    color: '#151515',
  },
  placeholder: {
    width: 24,
  },
  content: {
    flex: 1,
    paddingHorizontal: 24,
    paddingTop: 24,
  },
  description: {
    fontSize: 24,
    fontFamily: 'Sentient-Regular',
    color: '#151515',
    lineHeight: 28.8,
    marginBottom: 24,
  },
  currentPhoneContainer: {
    backgroundColor: '#F9F8F3',
    borderWidth: 1,
    borderColor: '#F0EEE4',
    padding: 16,
    borderRadius: 12,
    marginBottom: 24,
  },
  currentPhoneLabel: {
    fontSize: 12,
    fontFamily: 'Inter_500Medium',
    color: '#757C85',
    marginBottom: 4,
  },
  currentPhone: {
    fontSize: 16,
    fontFamily: 'Inter_500Medium',
    color: '#151515',
  },
  form: {
    gap: 24,
  },
  inputGroup: {
    gap: 8,
  },
  label: {
    color: '#151515',
    fontFamily: 'Inter',
    fontWeight: '700',
    fontSize: 14,
    lineHeight: 14,
  },
  input: {
    borderWidth: 1,
    borderColor: '#E7EAED',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
    fontFamily: 'Inter_400Regular',
    color: '#151515',
    backgroundColor: '#FFFFFF',
  },
  inputHint: {
    fontSize: 12,
    fontFamily: 'Inter',
    fontWeight: '400',
    color: '#A19F97',
  },
  footer: {
    paddingHorizontal: 24,
    paddingTop: 8,
  },
});
